import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Check, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

const EMOJI = { love: "👍", adore: "❤️", amen: "🙏", gloire: "🙌", sad: "😢", rire: "😂", wow: "😮" };

// Bouton « Suivre » → « Abonné » (façon X) d'une ligne de la liste ; jamais sur soi-même ni pour un visiteur.
const FollowChip = ({ user, initial, t }) => {
  const [following, setFollowing] = useState(!!initial);
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    const next = !following;
    setFollowing(next);
    try { await (next ? api.post(`/users/${user.id}/follow`) : api.delete(`/users/${user.id}/follow`)); }
    catch (err) { setFollowing(!next); toast.error(err?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy}
      className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${following ? "border" : "text-white"}`}
      style={following ? { borderColor: "var(--yalola-border-strong)", color: "var(--yalola-text)" } : { background: "var(--yalola-primary)" }}
      aria-pressed={following} data-testid={`reactor-follow-${user.id}`} data-following={following ? "1" : "0"}>
      {following ? <><Check size={13} /> {t("suggestions.followed", "Suivi(e)")}</> : <><UserPlus size={13} /> {t("suggestions.follow", "Suivre")}</>}
    </button>
  );
};

// Shared "who reacted" / "reposted by" list — post reactions, comment reactions, reposts.
// `title` may contain {n} (replaced by the count) ; otherwise « · N » is appended.
export const ReactorsDialog = ({ open, onOpenChange, endpoint, testId = "reactors-dialog", title, emptyLabel }) => {
  const { t, user: me } = useApp();
  const navigate = useNavigate();
  const [reactors, setReactors] = useState(null);

  useEffect(() => {
    if (!open || !endpoint) return;
    let alive = true;
    setReactors(null);
    api.get(endpoint)
      .then(({ data }) => { if (alive) setReactors(data.reactors || []); })
      .catch(() => { if (alive) setReactors([]); });
    return () => { alive = false; };
  }, [open, endpoint]);

  const base = title || t("reactions.who_title", "Qui a réagi");
  const n = reactors ? reactors.length : null;
  const heading = base.includes("{n}") ? base.replace("{n}", n === null ? "…" : String(n)) : `${base}${n === null ? "" : ` · ${n}`}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* z-130 : au-dessus des visionneurs photo/vidéo plein écran (z-100), comme la feuille Partager. */}
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col z-[130]" overlayClassName="z-[130]" data-testid={testId}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} data-testid={`${testId}-title`}>{heading}</DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto -mx-2 px-2">
          {reactors === null ? (
            <div className="py-8 grid place-items-center" data-testid={`${testId}-loading`}>
              <Loader2 size={22} className="animate-spin" style={{ color: "var(--yalola-text-muted)" }} />
            </div>
          ) : reactors.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-empty`}>
              {emptyLabel || t("reactions.who_empty", "Aucune réaction pour l'instant")}
            </div>
          ) : (
            reactors.map((r, i) => (
              <div key={`${r.user.id}-${i}`} className="flex items-center gap-3 px-1 py-2 rounded-xl hover:bg-[color:var(--yalola-surface-alt)] transition-colors" data-testid={`reactor-row-${r.user.id}`}>
                <button
                  type="button"
                  onClick={() => { onOpenChange?.(false); navigate(`/profile/${r.user.id}`); }}
                  className="flex-1 min-w-0 flex items-center gap-3 text-left"
                  data-testid={`reactor-${r.user.id}`}
                >
                  <div className="relative shrink-0">
                    <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(r.user)} /><AvatarFallback>{r.user.name?.[0]}</AvatarFallback></Avatar>
                    {r.type && <span className="absolute -bottom-1 -right-1 text-[16px] leading-none">{EMOJI[r.type] || "👍"}</span>}
                  </div>
                  <span className="text-sm font-semibold flex items-center gap-1 min-w-0 truncate">
                    {r.user.name}<VerifiedBadge user={r.user} size={12} />
                  </span>
                </button>
                {me && me.id !== r.user.id && <FollowChip user={r.user} initial={r.is_following} t={t} />}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReactorsDialog;
